import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { addNewLOB, isAxiosError } from '../../api';

// Components
import Textbox from '../../components/Textbox';
import ConfirmationModal from '../../components/ConfirmationModal';
import Select, { SelectOption, SingleSelection } from '../../components/Select';

// Types
import { NewLOBData, POSTErrorRes } from 'types';

// Utils
import { getClientNameError } from 'utils';

const FORM_ID = 'add-lob-form';

interface NewLineOfBusinessModalProps {
  agencyValue: SingleSelection;
  agencyOptions: SelectOption[];
  clientValue: SingleSelection;
  clientOptions: SelectOption[];
  onChangeAgency: (value: SingleSelection) => void;
  onChangeClient: (value: SingleSelection) => void;
  onCancel: () => void;
  onSubmit: (newLOB: NewLOBData | null) => void;
}

function NewLineOfBusinessModal({
  clientValue,
  agencyValue,
  clientOptions,
  agencyOptions,
  onChangeAgency,
  onChangeClient,
  onCancel,
  onSubmit,
}: NewLineOfBusinessModalProps) {
  const [lineOfBusiness, setLineOfBusiness] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { mutate, isLoading } = useMutation({
    mutationFn: () => {
      if (!agencyValue?.value) {
        throw new Error('Agency is required.');
      }
      if (!clientValue?.value) {
        throw new Error('Client is required.');
      }
      if (!lineOfBusiness) {
        throw new Error('Line of Business is required.');
      }
      return addNewLOB(parseInt(clientValue.value), lineOfBusiness);
    },
    onSuccess: (res) => {
      toast.success(res.message);
      onSubmit(res.data);
    },
    onError: (res) => {
      let message = 'An unknown error occurred.';
      if (isAxiosError<POSTErrorRes>(res)) {
        message = res.response?.data.error.message || message;
      }
      toast.error(message);
    },
  });

  const onChangeLOB = (value: string) => {
    setErrorMsg(getClientNameError(value));
    setLineOfBusiness(value);
  };

  const onSubmitForm = (evt: React.FormEvent) => {
    evt.preventDefault();
    mutate();
  };

  const onBlurValidation = (
    evt: React.FocusEvent<HTMLSelectElement | HTMLInputElement, Element>
  ) => {
    const { value } = evt.currentTarget;

    const clientNameError = getClientNameError(value);
    if (clientNameError) {
      setErrorMsg(clientNameError);
    } else {
      if (errorMsg) {
        setErrorMsg(null);
      }
    }
  };

  // JSX
  return (
    <ConfirmationModal
      formID={FORM_ID}
      header="Add Line of Business"
      content={
        <form
          id={FORM_ID}
          className="w-96 flex flex-col gap-6"
          onSubmit={onSubmitForm}
        >
          <Select
            value={agencyValue}
            label="Agency"
            options={agencyOptions}
            onChange={onChangeAgency}
            required
          />
          <Select
            value={clientValue}
            label="Client"
            options={clientOptions}
            onChange={onChangeClient}
            required
          />
          <Textbox
            label="Line of Business"
            value={lineOfBusiness}
            onChange={onChangeLOB}
            onBlur={onBlurValidation}
            errorMessage={errorMsg}
            required
          />
        </form>
      }
      onCancel={onCancel}
      isDisabled={!!errorMsg}
      isLoading={isLoading}
      isOpen={true}
      isForm
    />
  );
}

export default NewLineOfBusinessModal;
